import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import DadosGerais from '@/components/ClientDetails/DadosGerais';
import FaturasConsumo from '@/components/ClientDetails/FaturasConsumo';
import Fotos from '@/components/ClientDetails/Fotos';
import Projetos from '@/components/ClientDetails/Projetos';
import Propostas from '@/components/ClientDetails/Propostas';
import Documentos from '@/components/ClientDetails/Documentos';
import Contratos from '@/components/ClientDetails/Contratos';
import Financeiro from '@/components/ClientDetails/Financeiro';
import Tarefas from '@/components/ClientDetails/Tarefas';
import Contatos from '@/components/ClientDetails/Contatos';
import { ExtendedLead } from '@/components/ClientDetails/types';
import { Button } from "@/components/ui/button";
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { useAuthorization } from '@/contexts/AuthorizationContext';
import { isProdutoLeadCarregador } from '@/constants/leadsProduto';

interface ClientDetailsProps {
  cliente: ExtendedLead;
  onClose: () => void;
  onUpdate: () => void;
  defaultIsEditing?: boolean;
  /** Quando true, parceiro pai visualizando lead da rede: apenas leitura, sem editar/excluir */
  readOnly?: boolean;
}

export default function ClientDetails({ 
  cliente, 
  onClose, 
  onUpdate: propsOnUpdate,
  defaultIsEditing = false,
  readOnly = false
}: ClientDetailsProps) {
  const { hasRole } = useAuthorization();
  const isParceiro = hasRole('parceiro');
  const [activeTab, setActiveTab] = useState('dados-gerais');
  const [leadData, setLeadData] = useState<ExtendedLead>(cliente);
  const isCarregadores = isProdutoLeadCarregador(leadData.produto);
  const [hasUnsavedInDadosGerais, setHasUnsavedInDadosGerais] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);

  const refreshLead = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/leads/${cliente.id}`);
      if (!response.ok) {
        throw new Error('Erro ao buscar lead');
      }
      const data = await response.json();
      setLeadData(data);
    } catch (error) {
      console.error('Erro ao atualizar lead:', error);
    }
  }, [cliente.id]);

  useEffect(() => {
    refreshLead();
  }, [cliente.id, refreshLead]);

  // Se o status mudou para Lead e estava na aba financeiro, redirecionar para dados-gerais
  useEffect(() => {
    if (leadData.status !== 'Cliente' && activeTab === 'financeiro') {
      setActiveTab('dados-gerais');
    }
  }, [leadData.status, activeTab]);

  // Se for parceiro e estiver na aba projetos, redirecionar para dados-gerais
  useEffect(() => {
    if (isParceiro && activeTab === 'projetos') {
      setActiveTab('dados-gerais');
    }
  }, [isParceiro, activeTab]);

  const handleTabChange = (newValue: string) => {
    // Se tentar acessar financeiro e não for cliente, redirecionar para dados-gerais
    if (newValue === 'financeiro' && leadData.status !== 'Cliente') {
      return;
    }
    
    // Se for parceiro e tentar acessar projetos, bloquear
    if (isParceiro && newValue === 'projetos') {
      return;
    }
    
    if (activeTab === 'dados-gerais' && hasUnsavedInDadosGerais && newValue !== 'dados-gerais') {
      setPendingTab(newValue);
      setShowConfirm(true);
      return;
    }
    setActiveTab(newValue);
  };

  const localOnUpdate = async () => {
    await refreshLead();
    propsOnUpdate();
  };

  const handleDeleteLead = async () => {
    try {
      const response = await fetchWithAuth(`/api/leads/${cliente.id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Erro ao excluir lead');
      }
      
      const result = await response.json();
      alert(`Lead "${result.lead_name}" excluído com sucesso!`);
      onClose();
      propsOnUpdate();
    } catch (error) {
      console.error('Erro ao excluir lead:', error);
      alert('Erro ao excluir lead. Tente novamente.');
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Dados do Cliente: {cliente.nome || cliente.razaoSocial}</DialogTitle>
        <DialogDescription>
          {isCarregadores 
            ? "Visualize e gerencie as informações do cliente, incluindo dados gerais, fotos, documentos e contratos."
            : "Visualize e gerencie todas as informações, faturas, projetos e documentos do cliente."}
        </DialogDescription>
      </DialogHeader>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-4">
        {isCarregadores ? (
          <TabsList className="flex w-full flex-wrap justify-start gap-2 mb-6 min-h-auto h-auto">
            <TabsTrigger 
              value="dados-gerais" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Dados Gerais
            </TabsTrigger>
            <TabsTrigger
              value="contatos"
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Contatos
            </TabsTrigger>
            <TabsTrigger
              value="tarefas"
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Tarefas
            </TabsTrigger>
            <TabsTrigger 
              value="fotos" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Fotos
            </TabsTrigger>
            <TabsTrigger 
              value="documentos" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Documentos
            </TabsTrigger>
          </TabsList>
        ) : (
          <TabsList className="flex w-full flex-wrap justify-start gap-2 mb-6 min-h-auto h-auto">
            <TabsTrigger 
              value="dados-gerais" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Dados Gerais
            </TabsTrigger>
            <TabsTrigger
              value="contatos"
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Contatos
            </TabsTrigger>
            <TabsTrigger 
              value="tarefas" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Tarefas
            </TabsTrigger>
            <TabsTrigger 
              value="faturas-consumo" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Faturas e Consumo
            </TabsTrigger>
            <TabsTrigger 
              value="fotos" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Fotos
            </TabsTrigger>
            {!isParceiro && (
              <TabsTrigger 
                value="projetos" 
                className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                Projetos
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="documentos" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Documentos
            </TabsTrigger>
            <TabsTrigger 
              value="propostas" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Propostas
            </TabsTrigger>
            <TabsTrigger 
              value="contratos" 
              className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
            >
              Contratos
            </TabsTrigger>
            {leadData.status === 'Cliente' && (
              <TabsTrigger 
                value="financeiro" 
                className="flex-1 min-w-[150px] data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                Financeiro
              </TabsTrigger>
            )}
          </TabsList>
        )}

        <TabsContent value="dados-gerais" className="mt-0">
          <DadosGerais 
            leadData={leadData} 
            onUpdate={localOnUpdate} 
            onUnsavedChanges={setHasUnsavedInDadosGerais} 
            onDelete={readOnly ? () => {} : handleDeleteLead}
            defaultIsEditing={readOnly ? false : defaultIsEditing}
            readOnly={readOnly}
          />
        </TabsContent>
        <TabsContent value="contatos" className="mt-0">
          <Contatos leadData={leadData} onUpdate={localOnUpdate} readOnly={readOnly} />
        </TabsContent>
        <TabsContent value="tarefas" className="mt-0">
          <Tarefas leadData={leadData} onUpdate={localOnUpdate} readOnly={readOnly} />
        </TabsContent>
        <TabsContent value="faturas-consumo" className="mt-0">
          <FaturasConsumo leadData={leadData} onUpdate={localOnUpdate} />
        </TabsContent>
        <TabsContent value="fotos" className="mt-0">
          <Fotos leadData={leadData} onUpdate={localOnUpdate} />
        </TabsContent>
        {!isParceiro && (
          <TabsContent value="projetos" className="mt-0">
            <Projetos leadData={leadData} onUpdate={localOnUpdate} />
          </TabsContent>
        )}
        <TabsContent value="documentos" className="mt-0">
          <Documentos leadData={leadData} onUpdate={localOnUpdate} />
        </TabsContent>
        <TabsContent value="propostas" className="mt-0">
          <Propostas leadData={leadData} />
        </TabsContent>
        <TabsContent value="contratos" className="mt-0">
          <Contratos leadData={leadData} />
        </TabsContent>
        {leadData.status === 'Cliente' && (
          <TabsContent value="financeiro" className="mt-0">
            <Financeiro leadData={leadData} />
          </TabsContent>
        )}
      </Tabs>
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterações não salvas</DialogTitle>
            <DialogDescription>
              Por favor, salve ou cancele as alterações antes de mudar de aba. Deseja sair sem salvar?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancelar</Button>
            <Button onClick={() => {
              if (pendingTab) {
                setHasUnsavedInDadosGerais(false);
                setActiveTab(pendingTab);
              }
              setShowConfirm(false);
            }}>Sair sem salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
